import { create } from 'zustand';
import { shallow } from 'zustand/shallow';

export interface Caret {
  blockId: string;
  runId: string;
  offset: number; // grapheme offset
}

export interface LexicalRangeLike {
  blockId: string;
  runId: string;
  start: number;
  end: number; // exclusive
}

type SelectionSource = 'lexical' | 'canvas' | 'api';

interface SelectionState {
  anchor: Caret | null;
  focus: Caret | null;
  isCollapsed: boolean;

  lexicalRanges: LexicalRangeLike[];
  rangesSignature: string;

  selectionVersion: number;
  source: SelectionSource;

  pendingCaret: (Caret & { at: number }) | null;
  isComposing: boolean;
  isDragging: boolean;

  lastPlanHashUsedForMap: string | null;
  mappingInFlight: boolean;
  mapAbort?: AbortController;

  setSelection: (args: { anchor: Caret | null; focus: Caret | null; source: SelectionSource }) => void;
  setPendingCaret: (t: Caret | null) => void;
  commitPendingCaret: () => Caret | null;
  setComposing: (v: boolean) => void;
  setDragging: (v: boolean) => void;
  setPlanHashForMap: (planHash: string | null) => void;
  beginMapping: () => AbortSignal | null;
  endMapping: () => void;
}

function toRanges(anchor: Caret | null, focus: Caret | null): LexicalRangeLike[] {
  if (!anchor || !focus) return [];
  const sameRun = anchor.blockId === focus.blockId && anchor.runId === focus.runId;
  if (sameRun) {
    const start = Math.min(anchor.offset, focus.offset);
    const end = Math.max(anchor.offset, focus.offset);
    if (end <= start) return [];
    return [{ blockId: anchor.blockId, runId: anchor.runId, start, end }];
  }
  // MVP: represent as two separate ranges; multi-range stitching can be added later
  const r1: LexicalRangeLike = { blockId: anchor.blockId, runId: anchor.runId, start: anchor.offset, end: anchor.offset + 1 };
  const r2: LexicalRangeLike = { blockId: focus.blockId, runId: focus.runId, start: focus.offset, end: focus.offset + 1 };
  return [r1, r2];
}

function normalizeRanges(ranges: LexicalRangeLike[]): LexicalRangeLike[] {
  const cleaned = ranges
    .map(r => {
      let s = Math.max(0, r.start | 0);
      let e = Math.max(0, r.end | 0);
      if (e < s) [s, e] = [e, s];
      return { blockId: r.blockId, runId: r.runId, start: s, end: e };
    })
    .filter(r => r.blockId && r.runId && r.end > r.start);

  cleaned.sort((a, b) =>
    a.blockId === b.blockId
      ? (a.runId === b.runId ? a.start - b.start : a.runId.localeCompare(b.runId))
      : a.blockId.localeCompare(b.blockId)
  );

  const out: LexicalRangeLike[] = [];
  for (const r of cleaned) {
    const last = out[out.length - 1];
    if (last && last.blockId === r.blockId && last.runId === r.runId && r.start <= last.end) {
      last.end = Math.max(last.end, r.end);
    } else {
      out.push(r);
    }
  }
  return out;
}

function signature(ranges: LexicalRangeLike[]): string {
  if (ranges.length === 0) return '';
  return ranges.map(r => `${r.blockId}:${r.runId}:${r.start}-${r.end}`).join('|');
}

export const useSelectionStore = create<SelectionState>((set, get) => ({
  anchor: null,
  focus: null,
  isCollapsed: true,

  lexicalRanges: [],
  rangesSignature: '',

  selectionVersion: 0,
  source: 'lexical',

  pendingCaret: null,
  isComposing: false,
  isDragging: false,

  lastPlanHashUsedForMap: null,
  mappingInFlight: false,
  mapAbort: undefined,

  setSelection: ({ anchor, focus, source }) => {
    const ranges = normalizeRanges(toRanges(anchor, focus));
    const sig = signature(ranges);
    const prev = get();

    const sameAnchor = (!!prev.anchor && !!anchor && shallow(prev.anchor, anchor)) || (!prev.anchor && !anchor);
    const sameFocus = (!!prev.focus && !!focus && shallow(prev.focus, focus)) || (!prev.focus && !focus);
    if (prev.rangesSignature === sig && sameAnchor && sameFocus && prev.source === source) return;

    set({
      anchor,
      focus,
      isCollapsed: !!anchor && !!focus && anchor.blockId === focus.blockId && anchor.runId === focus.runId && anchor.offset === focus.offset,
      lexicalRanges: ranges,
      rangesSignature: sig,
      selectionVersion: prev.selectionVersion + 1,
      source,
    });
  },

  setPendingCaret: (t) => set({ pendingCaret: t ? { ...t, at: Date.now() } : null }),

  commitPendingCaret: () => {
    const p = get().pendingCaret;
    if (!p) return null;
    set({
      anchor: p, focus: p, isCollapsed: true,
      lexicalRanges: [], rangesSignature: '',
      selectionVersion: get().selectionVersion + 1,
      source: 'canvas',
      pendingCaret: null,
    });
    return p;
  },

  setComposing: (v) => set({ isComposing: v }),
  setDragging: (v) => set({ isDragging: v }),

  setPlanHashForMap: (planHash) => set({ lastPlanHashUsedForMap: planHash }),

  beginMapping: () => {
    if (get().mappingInFlight) {
      get().mapAbort?.abort();
    }
    const ctl = new AbortController();
    set({ mappingInFlight: true, mapAbort: ctl });
    return ctl.signal;
  },

  endMapping: () => set({ mappingInFlight: false, mapAbort: undefined }),
}));


